import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { AnnulationCertificat, Langue, MotifPublicAnnulation } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import type { CartelLigneFacture } from './ventes.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';

/**
 * Documents de galerie — fiche d'œuvre, certificats, listes de prix, dossier
 * de portabilité (`docs/dev/api.md` § 19, S4-c). Ces routes se greffent sur
 * les œuvres, expositions, ventes et contacts plutôt que de former un module
 * à elles seules ; ce fichier-ci les regroupe côté régie, précisément parce
 * que le geste, lui, est toujours « imprimer/émettre depuis la fiche ».
 *
 * Toutes rendent une URL SIGNÉE de 5 minutes, jamais le PDF lui-même (7.2) :
 * chaque appel ci-dessous est donc à faire au moment du clic, pas à l'avance.
 */

export interface ReponseDocumentSigne {
  readonly url: string;
  readonly expireLe: string;
  readonly expireDansSecondes: number;
  readonly nomFichier: string;
}

export interface SignataireCertificat {
  readonly nom: string;
  readonly qualite?: string;
}

/** Un certificat émis (§ 19.5) : le cartel et le signataire sont FIGÉS. */
export interface CertificatPresente {
  readonly id: string;
  readonly numero: string;
  readonly adresseVerification: string;
  readonly annuleLe: string | null;
  readonly motifPublic: MotifPublicAnnulation | null;
  readonly oeuvreId: string;
  readonly exemplaireId: string | null;
  readonly exemplaire: string | null;
  readonly langue: Langue;
  readonly emisLe: string;
  readonly cartel: CartelLigneFacture;
  readonly provenance: readonly string[];
  readonly signataire: SignataireCertificat;
  readonly lieuEmission: string | null;
  /** Le PDF est-il déjà déposé ? Sinon il se rend à la demande. */
  readonly documentPret: boolean;
}

export interface ReponseCertificats {
  readonly certificats: readonly CertificatPresente[];
  readonly total: number;
}

export interface CorpsEmissionCertificat {
  langue?: Langue;
  /** Exemplaire attesté (« 3/8 ») ; à défaut, le tirage de l'œuvre. */
  exemplaire?: string;
  exemplaireId?: string;
  provenance?: string[];
  lieuEmission?: string;
}

export interface ReponseEmissionCertificat {
  readonly certificat: CertificatPresente;
  readonly document: ReponseDocumentSigne;
}

/** Nombre demandé et créations réelles en file, sans preuve de PDF prêt. */
export interface ReponsePreparationDocuments {
  readonly total: number;
  readonly enfiles: number;
}

function requeteLangue(langue?: Langue): string {
  return langue ? `?langue=${langue}` : '';
}

export const documentsGalerieApi = {
  // ── Fiche d'œuvre (§ 19.4) ─────────────────────────────────────────────────
  fichePdf: (galerie: string, oeuvreId: string, langue?: Langue, appel?: AppelApi) =>
    api.get<ReponseDocumentSigne>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${oeuvreId}/fiche.pdf${requeteLangue(langue)}`,
      { galerie, ...appel },
    ),

  // ── Certificats (§ 19.5) ────────────────────────────────────────────────────
  certificats: (galerie: string, oeuvreId: string, appel?: AppelApi) =>
    api.get<ReponseCertificats>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${oeuvreId}/certificats`,
      { galerie, ...appel },
    ),
  /**
   * Re-télécharger un certificat DÉJÀ émis (§ 19.5). Lire n'est pas signer :
   * `documents:read` suffit, rien n'est réémis ni renuméroté — seul le PDF,
   * qui est un cache régénérable, est resservi.
   */
  certificatPdf: (galerie: string, oeuvreId: string, certificatId: string, appel?: AppelApi) =>
    api.get<ReponseDocumentSigne>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${oeuvreId}/certificats/${certificatId}/pdf`,
      { galerie, ...appel },
    ),
  annulerCertificat: (
    galerie: string,
    oeuvreId: string,
    certificatId: string,
    corps: AnnulationCertificat,
    appel?: AppelApi,
  ) =>
    api.post<{ certificat: CertificatPresente }>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${oeuvreId}/certificats/${certificatId}/annulation`,
      corps,
      { galerie, ...appel },
    ),
  emettreCertificat: (
    galerie: string,
    oeuvreId: string,
    corps: CorpsEmissionCertificat = {},
    appel?: AppelApi,
  ) =>
    api.post<ReponseEmissionCertificat>(
      `/g/${encodeURIComponent(galerie)}/oeuvres/${oeuvreId}/certificat`,
      corps,
      { galerie, ...appel },
    ),

  // ── Listes de prix (§ 19.6) ─────────────────────────────────────────────────
  listeDePrixExpositionPdf: (
    galerie: string,
    expositionId: string,
    langue?: Langue,
    appel?: AppelApi,
  ) =>
    api.get<ReponseDocumentSigne>(
      `/g/${encodeURIComponent(galerie)}/expositions/${expositionId}/liste-de-prix.pdf${requeteLangue(langue)}`,
      { galerie, ...appel },
    ),
  listeDePrixSelectionPdf: (
    galerie: string,
    oeuvres: readonly string[],
    langue?: Langue,
    appel?: AppelApi,
  ) => {
    const params = new URLSearchParams();
    params.set('oeuvres', oeuvres.join(','));
    if (langue) params.set('langue', langue);
    return api.get<ReponseDocumentSigne>(
      `/g/${encodeURIComponent(galerie)}/ventes/liste-de-prix.pdf?${params.toString()}`,
      { galerie, ...appel },
    );
  },

  /** Demande la liste et les fiches dans la langue par défaut de la galerie. */
  preparerDocumentsExposition: (galerie: string, expositionId: string, appel?: AppelApi) =>
    api.post<ReponsePreparationDocuments>(
      `/g/${encodeURIComponent(galerie)}/expositions/${encodeURIComponent(expositionId)}/documents/preparer`,
      {},
      { galerie, ...appel },
    ),

  // ── Portabilité en PDF (§ 19.7) ─────────────────────────────────────────────
  exportContactPdf: (galerie: string, contactId: string, langue?: Langue, appel?: AppelApi) =>
    api.get<ReponseDocumentSigne>(
      `/g/${encodeURIComponent(galerie)}/contacts/${contactId}/export.pdf${requeteLangue(langue)}`,
      { galerie, ...appel },
    ),
};

// ── Hooks React Query ────────────────────────────────────────────────────────

const cleCertificats = (galerie: string, oeuvreId: string) =>
  cleMetier(galerie, 'documents', 'certificats', oeuvreId);

export function useCertificatsOeuvre(galerie: string, oeuvreId: string | null) {
  return useQuery({
    queryKey: cleCertificats(galerie, oeuvreId ?? ''),
    queryFn: (contexte) =>
      documentsGalerieApi.certificats(galerie, oeuvreId ?? '', appelDeRequete(contexte)),
    enabled: oeuvreId !== null,
  });
}

/**
 * Ouvre le PDF d'un certificat déjà émis, dans un onglet neuf.
 *
 * Une URL signée ne dure que cinq minutes : elle se demande AU CLIC, jamais à
 * l'avance, et ne se met donc pas en cache (même règle que le coffre, § 18.5).
 */
export function useOuvrirCertificatPdf(galerie: string) {
  return useMutation(
    dansLaPortee({
      mutationFn: ({ oeuvreId, certificatId }: { oeuvreId: string; certificatId: string }, appel) =>
        documentsGalerieApi.certificatPdf(galerie, oeuvreId, certificatId, appel),
    }),
  );
}

export function useEmettreCertificat(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        { oeuvreId, corps }: { oeuvreId: string; corps?: CorpsEmissionCertificat },
        appel,
      ) => documentsGalerieApi.emettreCertificat(galerie, oeuvreId, corps, appel),
      onSuccess: (_donnees, variables) => {
        void client.invalidateQueries({ queryKey: cleCertificats(galerie, variables.oeuvreId) });
      },
    }),
  );
}

export function useAnnulerCertificat(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        {
          oeuvreId,
          certificatId,
          corps,
        }: { oeuvreId: string; certificatId: string; corps: AnnulationCertificat },
        appel,
      ) => documentsGalerieApi.annulerCertificat(galerie, oeuvreId, certificatId, corps, appel),
      onSuccess: (_donnees, variables) =>
        client.invalidateQueries({ queryKey: cleCertificats(galerie, variables.oeuvreId) }),
    }),
  );
}
